<template>
  <!-- Enabled when we have enough params, otherwise a disabled button -->
  <router-link
      v-if="hasRequired"
      :to="to"
      class="btn btn-sm btn-outline-primary"
      target="_blank"
  >
    {{ label }}
  </router-link>
  <button
      v-else
      class="btn btn-sm btn-outline-primary"
      disabled
  >
    {{ label }}
  </button>
</template>

<script>
import { DateTime } from 'luxon';

export default {
  name: 'RawNotesDrillDownLink',
  props: {
    // Supported: 'notes-user' | 'notes-global'
    mode: { type: String, default: 'notes-user' },

    // From the criteria bar slot: { side, start, end, userId, includeOther, windowed, ... }
    params: { type: Object, required: true },

    // Normalized query from criteria bar
    query: { type: Object, default: () => ({}) },

    // Parity with CSV button (not used here, but harmless to keep)
    apiBaseUrl: { type: String, default: '/api/v1/' },

    label: { type: String, default: 'View notes' },

    // Frontend route names (override if your router names differ)
    routeNameUser:   { type: String, default: 'reports_notestats_user_notes_detailed' },
    routeNameGlobal: { type: String, default: 'reports_notestats_global_notes_detailed' },

    // Path date format used by your routes
    datePathFormat: { type: String, default: 'dd-LL-yyyy' },
  },

  computed: {
    routeName() {
      return this.mode === 'notes-user' ? this.routeNameUser : this.routeNameGlobal;
    },

    side() {
      const s = (this.params?.side || 'combined').toLowerCase();
      return ['sales', 'service', 'combined'].includes(s) ? s : 'combined';
    },

    userId() {
      return this.params && this.params.userId != null
          ? String(this.params.userId)
          : null;
    },

    hasRequired() {
      // For user notes: need start, end, and userId
      if (this.mode === 'notes-user') {
        return !!(this.params?.start && this.params?.end && this.userId);
      }
      // For global notes: need start and end
      return !!(this.params?.start && this.params?.end);
    },

    to() {
      // Only built when hasRequired = true (template guards with v-if anyway)
      if (this.mode === 'notes-user') {
        return {
          name: this.routeName,
          params: {
            userid: this.userId,
            type: this.side,
            start: this.pathDate(this.params.start),
            end:   this.pathDate(this.params.end),
          },
          query: this.sanitizedQuery,
        };
      }
      // notes-global
      return {
        name: this.routeName,
        params: {
          type: this.side,
          start: this.pathDate(this.params.start),
          end:   this.pathDate(this.params.end),
        },
        query: this.sanitizedQuery,
      };
    },

    sanitizedQuery() {
      // Merge current route query (if any) with provided query; provided wins
      const base = (this.$route && this.$route.query) ? { ...this.$route.query } : {};
      const raw = { ...base, ...(this.query || {}) };

      // Only pass through supported filters
      const allowed = ['mask', 'match', 'not', 'includeOther', 'windowed', 'perPage', 'limit', 'per_page'];
      const out = {};
      for (const k of allowed) {
        const v = raw[k];
        if (v !== undefined && v !== null && String(v) !== '') out[k] = v;
      }

      // normalize booleans → "1"/"0"
      if (out.includeOther !== undefined) out.includeOther = this.bool01(out.includeOther);
      if (out.windowed     !== undefined) out.windowed     = this.bool01(out.windowed);

      // never carry over 'page'
      delete out.page;

      return out;
    },
  },

  methods: {
    pathDate(text) {
      // Incoming is usually 'yyyy-LL-dd HH:mm:ss' (from criteria bar exposed params)
      let dt = DateTime.fromISO(String(text));
      if (!dt.isValid) dt = DateTime.fromFormat(String(text), 'yyyy-LL-dd HH:mm:ss');
      if (!dt.isValid) dt = DateTime.fromFormat(String(text), 'dd-LL-yyyy');
      if (!dt.isValid) dt = DateTime.local();
      // Router expects e.g. dd-LL-yyyy in the path
      return dt.toFormat(this.datePathFormat);
    },
    bool01(v) {
      if (typeof v === 'boolean') return v ? '1' : '0';
      const s = String(v).toLowerCase();
      if (['1', 'true', 'yes', 'on'].includes(s)) return '1';
      if (['0', 'false', 'no', 'off'].includes(s)) return '0';
      return s;
    },
  },
};
</script>
